import {useUser} from '@dofleini/security';
import {useKeycloak} from '@react-keycloak/web';
import {message} from 'antd';
import browserUpdate from 'browser-update';
import React, {useCallback, useEffect} from 'react';

import {AuthDropdownProvider} from '@/contexts/AuthDropdownContext';
import {ProposalByUserProvider} from '@/contexts/CheckUserAndProposal';
import {SidebarProvider} from '@/contexts/SidebarContext';
import useAlerts from '@/hooks/useAlerts';
import {useDatysLogin} from '@/modules/authentication/hooks/useAuthentication';
import {useMe} from '@/modules/authentication/hooks/useUsers';
import MainApp from '@/modules/MainApp';
import OnBoardingModule from '@/modules/onboarding';
import UnauthenticatedLayout from '@/modules/UnauthenticatedLayout';
import {authentication} from '@/settings/authentication';

const browserUpdateOptions =
  {
    required: {
      e: -4,
      i: 11,
      f: -7,
      o: -3,
      s: -1,
      c: -3,
      samsung: 7.0,
      vivaldi: 1.2
    },
    // Specifies required browser versions
    // Browsers older than this will be notified.
    // f:22 ---> Firefox < 22 gets notified
    // Negative numbers specify how much versions behind current version.
    // c:-5 ---> Chrome < 35  gets notified if latest Chrome version is 40.
    // more details (in english)
    
    unsupported: true,
    
    reminder: 24,
    // after how many hours should the message reappear
    // 0 = show all the time
    
    reminderClosed: 150,
    // if the user explicitly closes message it reappears after x hours
    
    l: 'es',
    // set a fixed language for the message, e.g. "en". This overrides the default detection.
    
    test: false,
    // true = always show the bar (for testing)
    
    style: 'bottom',
    
    text: {
      'msg': 'Su navegador web ({brow_name}) no está actualizado.',
      'msgmore': 'Actualícelo para mayor seguridad, velocidad y la mejor experiencia en la Plataforma de Actores Económicos.',
      'bupdate': 'Actualizar',
      'bignore': 'Ignorar',
      'remind': 'Se le recordará en {days} días.',
      'bnever': 'No mostrar de nuevo'
    },
    
    newwindow: true,
    // open link in new window/tab
    
    url: 'https://www.google.com/search?q=actualizar+mi+navegador',
    // the url to go to after clicking the notification
    
    noclose: false,
    // Do not show the "ignore" button to close the notification
    
    nomessage: false,
    // Do not show a message if the browser is out of date, just call the onshow callback function
    
    jsshowurl: '//browser-update.org/update.show.min.js',
    // URL where the script, that shows the notification, is located. This is only loaded if the user actually has an outdated browser.
    
    container: document.body,
    // DOM Element where the notification will be injected.
    
    no_permanent_hide: false,
    // Do not give the user the option to permanently hide the notification
    
    // api: xxx,
    // This is the version of the browser-update api to use. Please do not remove.
    insecure: true,
  };

function AppContent() {
  browserUpdate(browserUpdateOptions);
  const {user} = useUser();
  
  if (user && authentication?.activeOnboarding && !user?.onBoardingCompleted)
    return <OnBoardingModule/>;
  
  if (user)
    return <MainApp/>;
  
  return <UnauthenticatedLayout/>;
}

function App() {
  const {isAuthenticated} = useUser();
  const {refetch} = useMe();
  const {putErrorCode} = useAlerts();
  const {keycloak} = useKeycloak();
  const {onSignIn} = useDatysLogin(refetch);
  
  useEffect(() => {
    if (window.location.search.includes('code=') || window.location.search.includes('session_state=')) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  keycloak.onAuthSuccess = useCallback(() => {
    if (!isAuthenticated) {
      // check if the user has a completed certification
      if (keycloak.tokenParsed?.certification?.status)
        onSignIn({token: keycloak.token})
          .catch(putErrorCode);
      else
        message.warning('Es necesario completar la verificación en el proveedor de identidad para continuar con la autenticación.');
    }
  }, [isAuthenticated, keycloak.token, keycloak.tokenParsed?.certification?.status, onSignIn, putErrorCode]);
  
  return <AuthDropdownProvider>
    <SidebarProvider>
      <ProposalByUserProvider>
        <AppContent/>
      </ProposalByUserProvider>
    </SidebarProvider>
  </AuthDropdownProvider>;
}

export default App;
